import React, {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {Menu} from '@headlessui/react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
    Download,
    DollarSign,
    Globe,
    ImageOff,
    Loader2,
    Package,
    Pencil,
    Plus,
    Sparkles,
    Trash2,
    Upload,
} from 'lucide-react';
import Button from '@/shared/components/Button';
import RecommendedListPicker, {type PickerOption} from '@/shared/components/RecommendedListPicker';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {isUserAdmin} from '@/shared/utils/tokenUtils';
import AdminService from '@/modules/admin/services/adminService';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {downloadSqlFile} from '@/shared/utils/downloadFile';
import {logError} from '@/shared/utils/logger';
import type {StorefrontProduct, StorefrontProductInput} from '@/shared/types/api';

interface Props {
    /**
     * Required when `globalScope=false` — drives all per-org CRUD.
     * Optional when `globalScope=true` (super-admin global library);
     * any value passed is ignored.
     */
    orgId?: number;
    /**
     * Super-admin global authoring. Lists / edits products with
     * `organization_id IS NULL` (the MLD-curated catalog visible to
     * every tenant). Disables the per-org overlay UI bits (no
     * Active/Inactive dropdown, no price-override popover).
     */
    globalScope?: boolean;
}

const EMPTY_FORM: StorefrontProductInput = {
    name: '',
    description: '',
    long_description: '',
    category: 'At-Home Testing',
    image_url: '',
    price_cents: 0,
    member_price_cents: null,
    default_cadence_days: null,
    cadence_label: null,
    supply_per_shipment: null,
    profile_id: null,
    is_active: false,
    is_featured: false,
    sort_order: 0,
    sku: null,
    admin_notes: null,
    assays: [],
};

/**
 * Subscription cadence presets for the Subscription section. Custom
 * lets the admin enter any day count for an off-cycle ship schedule.
 */
const CADENCE_OPTIONS: Array<{value: number | null; label: string; days: number | null}> = [
    {value: null, label: 'One-time only (no subscription)', days: null},
    {value: 30, label: 'Monthly (every 30 days)', days: 30},
    {value: 90, label: 'Quarterly (every 90 days)', days: 90},
    {value: 180, label: 'Every 6 months', days: 180},
    {value: 365, label: 'Annual (every 365 days)', days: 365},
];

const StorefrontProductBuilder: React.FC<Props> = ({orgId, globalScope}) => {
    const queryClient = useQueryClient();
    const {user} = useAuth();
    // Role-gated (not scope-gated): a super admin editing an org's
    // products still sees SKU / assays / notes; org admins never do.
    // The API enforces the same rule server-side.
    const isSuperAdmin = isUserAdmin(user?.token);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<StorefrontProductInput>({...EMPTY_FORM});
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [priceInput, setPriceInput] = useState('');
    const [memberPriceInput, setMemberPriceInput] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageMode, setImageMode] = useState<'upload' | 'url'>('upload');
    // The upload/URL picker is collapsed once a product has an image — the
    // admin sees the image itself plus Replace / Remove. Replace reopens it.
    // Tracked explicitly (not derived from "has an image") so that typing a
    // URL, which produces an image the moment it parses, can't yank the
    // input out from under the cursor.
    const [pickerOpen, setPickerOpen] = useState(true);
    // Set when the current image_url fails to load, so we show a "can't load"
    // placeholder (with the URL) instead of a silently broken <img>.
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);

    const queryKey = globalScope
        ? (['admin', 'storefront-products', 'global'] as const)
        : (['admin', 'storefront-products', orgId ?? 0] as const);

    const {data: products = [], isLoading} = useQuery({
        queryKey,
        queryFn: () =>
            globalScope
                ? AdminService.listGlobalStorefrontProducts()
                : AdminService.listStorefrontProducts(orgId as number),
        staleTime: 30_000,
        enabled: globalScope || (orgId != null && orgId > 0),
    });

    // Fetch profiles for the dropdown — org-only concept; no-op in
    // global mode (super-admin can't link a product to an org's
    // private profile).
    const {data: catalogData} = useQuery({
        queryKey: ['admin-org-catalog', orgId],
        queryFn: () => AdminService.getOrganizationCatalog(orgId as number),
        staleTime: 60_000,
        enabled: !globalScope && orgId != null,
    });
    const profiles = catalogData?.profiles ?? [];

    // Assay catalog for the Included Assays picker — super-admin only,
    // fetched lazily when the modal opens. Same key/options shape as
    // the BioMatch builder so the cache is shared.
    const {data: assayCatalog = []} = useQuery({
        queryKey: ['admin', 'assay-catalog'],
        queryFn: () => AdminService.getAssayCatalog(),
        enabled: modalOpen && isSuperAdmin,
        staleTime: 5 * 60 * 1000,
    });

    const assayOptions: PickerOption[] = useMemo(() => {
        return assayCatalog
            .slice()
            .sort((a, b) => {
                const calcDelta = Number(a.is_calculated) - Number(b.is_calculated);
                if (calcDelta !== 0) return calcDelta;
                return a.name.localeCompare(b.name);
            })
            .map((a) => ({key: a.code, label: a.name, sublabel: a.is_calculated ? 'calculated' : undefined}));
    }, [assayCatalog]);

    // Profiles already linked to a product (can't be linked twice)
    const linkedProfileIds = new Set(products.filter((p) => p.profile_id).map((p) => p.profile_id!));

    // Effective orgId for service calls. Null in global mode signals
    // the no-orgId BE routes; org mode passes the actual orgId.
    const effectiveOrgId: number | null = globalScope ? null : (orgId ?? null);

    const createMutation = useMutation({
        mutationFn: (input: StorefrontProductInput) => AdminService.createStorefrontProduct(effectiveOrgId, input),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Product created');
            closeModal();
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to create product');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({id, input}: {id: number; input: Partial<StorefrontProductInput>}) =>
            AdminService.updateStorefrontProduct(effectiveOrgId, id, input),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Product updated');
            closeModal();
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to update product');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => AdminService.deleteStorefrontProduct(effectiveOrgId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Product deleted');
            setDeleteConfirmId(null);
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to delete product');
        },
    });

    /**
     * Per-org toggle for a global product viewed from an org. Writes
     * to `storefront_product_org_state` (enabled/disabled). The
     * actual `is_active` flag on the global row stays untouched —
     * super-admin controls that.
     */
    const orgStateToggleMutation = useMutation({
        mutationFn: ({productId, enabled}: {productId: number; enabled: boolean}) =>
            AdminService.upsertStorefrontProductOrgState(orgId as number, productId, {enabled}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Product visibility updated');
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to update product visibility');
        },
    });

    /**
     * Per-org price override for a global product. Pass null to clear
     * the override and revert to the global's default price.
     */
    const orgStatePriceMutation = useMutation({
        mutationFn: ({
            productId,
            price_override_cents,
            member_price_override_cents,
            billing_type,
            employer_coverage_percent,
        }: {
            productId: number;
            price_override_cents: number | null;
            member_price_override_cents: number | null;
            billing_type?: string;
            employer_coverage_percent?: number | null;
        }) =>
            AdminService.upsertStorefrontProductOrgState(orgId as number, productId, {
                price_override_cents,
                member_price_override_cents,
                billing_type,
                employer_coverage_percent,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            toast.success('Price override saved');
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.error ?? 'Failed to save price override');
        },
    });

    const toggleActive = useCallback(
        (product: StorefrontProduct) => {
            const isGlobalRow = product.organization_id == null;
            // From an org view, a global row toggles the per-org
            // overlay (writes to storefront_product_org_state). From
            // the super-admin global view, the same row toggles the
            // intrinsic is_active flag.
            if (isGlobalRow && !globalScope) {
                const currentEnabled = product.org_state?.enabled ?? true;
                orgStateToggleMutation.mutate({
                    productId: product.id,
                    enabled: !currentEnabled,
                });
                return;
            }
            updateMutation.mutate({id: product.id, input: {is_active: !product.is_active}});
        },
        [updateMutation, orgStateToggleMutation, globalScope]
    );

    // Object URLs for the staged-file preview have to be revoked by hand;
    // the ref is what lets us release the previous one on every swap.
    const previewObjectUrl = useRef<string | null>(null);

    /** Stages (or clears) a picked file and keeps its preview URL in sync. */
    const stageImageFile = useCallback((file: File | null) => {
        if (previewObjectUrl.current) {
            URL.revokeObjectURL(previewObjectUrl.current);
            previewObjectUrl.current = null;
        }
        setImageFile(file);
        if (!file) {
            setImagePreview(null);
            return;
        }
        const url = URL.createObjectURL(file);
        previewObjectUrl.current = url;
        setImagePreview(url);
        setImageLoadFailed(false);
    }, []);

    /** @param openPicker false when the product already has an image to show. */
    const resetImageState = useCallback(
        (openPicker: boolean) => {
            stageImageFile(null);
            setImageMode('upload');
            setPickerOpen(openPicker);
            setImageLoadFailed(false);
        },
        [stageImageFile]
    );

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditingId(null);
        setForm({...EMPTY_FORM});
        setPriceInput('');
        setMemberPriceInput('');
        resetImageState(true);
    }, [resetImageState]);

    const openCreate = useCallback(() => {
        setEditingId(null);
        setForm({...EMPTY_FORM});
        setPriceInput('');
        setMemberPriceInput('');
        resetImageState(true);
        setModalOpen(true);
    }, [resetImageState]);

    const openEdit = useCallback(
        (p: StorefrontProduct) => {
            setEditingId(p.id);
            setForm({
                name: p.name,
                description: p.description,
                long_description: p.long_description ?? '',
                category: p.category,
                image_url: p.image_url ?? '',
                price_cents: p.price_cents,
                member_price_cents: p.member_price_cents,
                default_cadence_days: p.default_cadence_days ?? null,
                cadence_label: p.cadence_label ?? null,
                supply_per_shipment: p.supply_per_shipment ?? null,
                profile_id: p.profile_id,
                is_active: p.is_active,
                is_featured: p.is_featured,
                sort_order: p.sort_order,
                sku: p.sku ?? null,
                admin_notes: p.admin_notes ?? null,
                assays: p.assays ?? [],
            });
            setPriceInput((p.price_cents / 100).toFixed(2));
            setMemberPriceInput(p.member_price_cents ? (p.member_price_cents / 100).toFixed(2) : '');
            // An existing image renders as a thumbnail, so the picker starts
            // closed and defaults to Upload when the admin opens it.
            resetImageState(!p.image_url);
            setModalOpen(true);
        },
        [resetImageState]
    );

    const uploadImageForProduct = useCallback(
        async (productId: number) => {
            if (imageFile) {
                await AdminService.uploadStorefrontProductImage(effectiveOrgId, productId, imageFile);
            }
        },
        [imageFile, effectiveOrgId]
    );

    const handleSubmit = useCallback(async () => {
        if (!form.name.trim()) {
            toast.error('Product name is required');
            return;
        }

        const priceCents = Math.round(parseFloat(priceInput || '0') * 100);
        const memberCents = memberPriceInput.trim() ? Math.round(parseFloat(memberPriceInput) * 100) : null;

        if (!Number.isFinite(priceCents) || priceCents < 0) {
            toast.error('Enter a valid price');
            return;
        }

        const input: StorefrontProductInput = {
            ...form,
            price_cents: priceCents,
            member_price_cents: memberCents,
            // A staged file wins: leave image_url alone here so the upload
            // that follows can set it. Otherwise send the form value —
            // an empty string is how Remove clears it (server → NULL).
            image_url: imageFile ? undefined : (form.image_url ?? ''),
        };
        if (!isSuperAdmin) {
            // Server rejects these fields from non-super-admins; org
            // admin payloads must not carry the untouched defaults.
            delete input.sku;
            delete input.admin_notes;
            delete input.assays;
        }

        setSaving(true);
        try {
            if (editingId != null) {
                await AdminService.updateStorefrontProduct(effectiveOrgId, editingId, input);
                await uploadImageForProduct(editingId);
            } else {
                const created = await AdminService.createStorefrontProduct(effectiveOrgId, input);
                await uploadImageForProduct(created.id);
            }
            queryClient.invalidateQueries({queryKey});
            toast.success(editingId ? 'Product updated' : 'Product created');
            closeModal();
        } catch (err: any) {
            toast.error(err?.response?.data?.error ?? 'Failed to save product');
        } finally {
            setSaving(false);
        }
    }, [
        form,
        priceInput,
        memberPriceInput,
        imageFile,
        editingId,
        orgId,
        isSuperAdmin,
        queryKey,
        queryClient,
        closeModal,
        uploadImageForProduct,
    ]);

    const isSaving = saving || createMutation.isPending || updateMutation.isPending;

    // --- Image field -------------------------------------------------------
    // A staged file's blob preview wins over the saved image_url; an empty
    // image_url with nothing staged means "no image" (or the admin removed it).
    const currentImageSrc = imagePreview ?? resolveMediaUrl(form.image_url);
    const hasImage = Boolean(currentImageSrc);
    const showImagePicker = pickerOpen || !hasImage;

    // Snapshot so Cancel can undo a URL the admin typed over the old one.
    const imageUrlBeforeReplace = useRef('');

    const startReplaceImage = useCallback(() => {
        imageUrlBeforeReplace.current = form.image_url ?? '';
        setPickerOpen(true);
    }, [form.image_url]);

    const cancelReplaceImage = useCallback(() => {
        stageImageFile(null);
        setForm((f) => ({...f, image_url: imageUrlBeforeReplace.current}));
        setPickerOpen(false);
        setImageLoadFailed(false);
    }, [stageImageFile]);

    /** Clears the image entirely — the save then nulls image_url server-side. */
    const removeImage = useCallback(() => {
        stageImageFile(null);
        setForm((f) => ({...f, image_url: ''}));
        setPickerOpen(true);
        setImageLoadFailed(false);
    }, [stageImageFile]);

    const handleProfileSelect = useCallback(
        (profileId: number | null) => {
            setForm((f) => ({...f, profile_id: profileId}));
            if (profileId && !form.name.trim()) {
                const profile = profiles.find((p) => p.id === profileId);
                if (profile?.name) {
                    setForm((f) => ({...f, name: profile.name!}));
                }
            }
        },
        [form.name, profiles]
    );

    // Export the admin-built globals for cross-env promotion. Mirrors the
    // BioMatch library export button UX (BioMatchManager.handleExport).
    const handleExport = async () => {
        setExporting(true);
        try {
            const result = await AdminService.exportGlobalStorefrontProducts();
            if (result.emailed) {
                toast.success(`Exported from ${result.environment} — emailed to ${result.recipients.join(' and ')}.`);
            } else {
                downloadSqlFile(result.filename, result.sql);
                toast.success(
                    `Email isn't configured here — downloaded ${result.filename} (from ${result.environment}) instead.`
                );
            }
        } catch (error) {
            logError('Failed to export global products', error);
            const message =
                (error as {data?: {error?: string}; response?: {data?: {error?: string}}})?.data?.error ??
                (error as {response?: {data?: {error?: string}}})?.response?.data?.error;
            toast.error(message || 'Could not export global products.');
        } finally {
            setExporting(false);
        }
    };

    // Sort: own (CUSTOM) first alphabetically, then globals
    // alphabetically. Mirrors the BioMatch list ordering.
    const sortedProducts = React.useMemo(() => {
        return [...products].sort((a, b) => {
            const aIsGlobal = a.organization_id == null ? 1 : 0;
            const bIsGlobal = b.organization_id == null ? 1 : 0;
            if (aIsGlobal !== bIsGlobal) return aIsGlobal - bIsGlobal;
            return a.name.localeCompare(b.name);
        });
    }, [products]);

    return (
        <div className="space-y-6">
            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-6 shadow-sm">
                {/* Section header — matches BioMatchManager pattern */}
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-mld-panel-border pb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-mld-text-primary">
                            {globalScope ? 'Global Storefront Products' : 'Storefront Products'}
                        </h2>
                        <p className="text-sm text-mld-text-secondary">
                            {globalScope
                                ? 'System-default products available to every organization. Each tenant can override price or deactivate per-org.'
                                : 'Manage products available for ordering via API and public storefront.'}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {globalScope && (
                            <Button
                                type="button"
                                variant="secondary"
                                loading={exporting}
                                onClick={handleExport}
                                iconLeft={<Download className="h-4 w-4" />}>
                                Export Global Products
                            </Button>
                        )}
                        <Button type="button" onClick={openCreate} iconLeft={<Plus className="h-4 w-4" />}>
                            {globalScope ? 'Add Global Product' : 'Add Product'}
                        </Button>
                    </div>
                </div>

                {/* Products list — row-card layout matching BioMatchList */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-5 w-5 animate-spin text-mld-muted-text" />
                    </div>
                ) : products.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-mld-panel-border py-10 text-center text-sm text-mld-muted-text">
                        <Package className="mx-auto mb-2 h-8 w-8 text-mld-muted-text" />
                        No storefront products yet. Add one to get started.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {sortedProducts.map((p) => {
                            const isGlobalRow = p.organization_id == null;
                            // Globals viewed from an org context are
                            // read-only at the row level — only the
                            // org-state toggle + price override are
                            // editable. Super-admin global view edits
                            // globals like own products.
                            const isReadOnlyForOrg = isGlobalRow && !globalScope;
                            // Effective enabled state:
                            //   own product: is_active
                            //   global from org: org_state.enabled (default TRUE)
                            //   global from super-admin: is_active
                            const effectiveEnabled = isReadOnlyForOrg ? (p.org_state?.enabled ?? true) : p.is_active;
                            const overriddenPrice = p.org_state?.price_override_cents ?? null;
                            const effectivePrice = overriddenPrice ?? p.price_cents;
                            return (
                                <div
                                    key={p.id}
                                    className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-4 transition hover:border-mld-primary/40">
                                    <div className="grid items-center gap-4 xl:grid-cols-[260px_1fr_auto_auto_auto]">
                                        {/* Name + pill */}
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold text-mld-text-primary">
                                                    {p.name}
                                                </div>
                                                <div className="truncate text-xs text-mld-muted-text">{p.category}</div>
                                                {isSuperAdmin && p.sku && (
                                                    <div className="truncate font-mono text-[10px] text-mld-muted-text">
                                                        SKU {p.sku}
                                                    </div>
                                                )}
                                            </div>
                                            {isGlobalRow ? (
                                                <span
                                                    title="Global product — managed by MLD super admins"
                                                    className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                                    <Globe className="h-2.5 w-2.5" />
                                                    Global
                                                </span>
                                            ) : (
                                                <span
                                                    title="Custom product — created by this organization"
                                                    className="inline-flex items-center gap-1 rounded-full border border-mld-info/20 bg-mld-info/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-mld-info">
                                                    <Sparkles className="h-2.5 w-2.5" />
                                                    Custom
                                                </span>
                                            )}
                                        </div>

                                        {/* Profile + cadence chips */}
                                        <div className="flex flex-wrap gap-2">
                                            {p.profile && (
                                                <span className="inline-flex items-center rounded-full border border-mld-panel-border bg-white px-3 py-1 text-xs font-medium text-mld-text-secondary">
                                                    <span className="font-semibold text-mld-text-secondary">
                                                        Profile
                                                    </span>
                                                    <span className="mx-1 text-mld-muted-text">·</span>
                                                    {p.profile.name}
                                                </span>
                                            )}
                                            {(p.cadence_label ||
                                                (p.default_cadence_days && `Every ${p.default_cadence_days} days`)) && (
                                                <span className="inline-flex items-center rounded-full border border-mld-panel-border bg-white px-3 py-1 text-xs font-medium text-mld-text-secondary">
                                                    {p.cadence_label ?? `Every ${p.default_cadence_days} days`}
                                                </span>
                                            )}
                                            {!p.default_cadence_days && (
                                                <span className="inline-flex items-center rounded-full border border-mld-panel-border bg-white px-3 py-1 text-xs font-medium text-mld-muted-text">
                                                    One-time
                                                </span>
                                            )}
                                        </div>

                                        {/* Price */}
                                        <div className="justify-self-end">
                                            <div className="inline-flex items-center gap-1.5 font-mono text-sm text-mld-text-secondary">
                                                <span className={overriddenPrice != null ? 'text-mld-primary' : ''}>
                                                    ${(effectivePrice / 100).toFixed(2)}
                                                </span>
                                                {overriddenPrice != null && (
                                                    <span className="text-[10px] text-mld-text-secondary line-through">
                                                        ${(p.price_cents / 100).toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status pill */}
                                        <div className="justify-self-end">
                                            <button
                                                type="button"
                                                onClick={() => toggleActive(p)}
                                                className={clsx(
                                                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition',
                                                    effectiveEnabled
                                                        ? 'bg-mld-success/5 text-mld-success hover:bg-mld-success/10'
                                                        : 'bg-mld-danger/5 text-mld-danger hover:bg-mld-danger/10'
                                                )}>
                                                {isReadOnlyForOrg
                                                    ? effectiveEnabled
                                                        ? 'Active'
                                                        : 'Inactive'
                                                    : effectiveEnabled
                                                      ? 'Published'
                                                      : 'Draft'}
                                            </button>
                                        </div>

                                        {/* Edit / Delete (own + super-admin) OR Override (org viewing a global) */}
                                        <div className="flex items-center gap-2 justify-self-end">
                                            {isReadOnlyForOrg ? (
                                                <PriceOverridePopover
                                                    product={p}
                                                    onSave={(price, member, billingType, coveragePct) =>
                                                        orgStatePriceMutation.mutate({
                                                            productId: p.id,
                                                            price_override_cents: price,
                                                            member_price_override_cents: member,
                                                            billing_type: billingType,
                                                            employer_coverage_percent: coveragePct,
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => openEdit(p)}
                                                        title="Edit"
                                                        className="inline-flex items-center gap-1 rounded-md bg-mld-warning/10 px-3 py-1 text-xs font-semibold text-mld-warning hover:bg-mld-warning hover:text-white">
                                                        <Pencil className="h-3 w-3" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeleteConfirmId(p.id)}
                                                        title="Delete"
                                                        className="inline-flex items-center gap-1 rounded-md bg-mld-danger/5 px-3 py-1 text-xs font-semibold text-mld-danger hover:bg-mld-danger hover:text-white">
                                                        <Trash2 className="h-3 w-3" />
                                                        <span>Delete</span>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create / Edit Modal */}
            <Transition appear show={modalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="border-b border-mld-panel-border px-6 py-4">
                                        <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                            {editingId != null ? 'Edit Product' : 'Add Storefront Product'}
                                        </Dialog.Title>
                                    </div>

                                    <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-5">
                                        {/* Profile link */}
                                        {editingId == null && (
                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Linked Profile{' '}
                                                    <span className="font-normal text-mld-muted-text">(optional)</span>
                                                </label>
                                                <select
                                                    value={form.profile_id ?? ''}
                                                    onChange={(e) =>
                                                        handleProfileSelect(
                                                            e.target.value ? Number(e.target.value) : null
                                                        )
                                                    }
                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                                                    <option value="">No profile (standalone product)</option>
                                                    {profiles.map((p) => (
                                                        <option
                                                            key={p.id}
                                                            value={p.id}
                                                            disabled={linkedProfileIds.has(p.id)}>
                                                            {p.name}
                                                            {linkedProfileIds.has(p.id) ? ' (already linked)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Name */}
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Product Name
                                            </label>
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setForm((f) => ({...f, name: e.target.value}))}
                                                placeholder="e.g. Total Testosterone Test Kit"
                                                className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>

                                        {/* Description */}
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Description
                                            </label>
                                            <textarea
                                                value={form.description}
                                                onChange={(e) => setForm((f) => ({...f, description: e.target.value}))}
                                                rows={2}
                                                placeholder="Short product description"
                                                className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>

                                        {/* Category */}
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                Category
                                            </label>
                                            <input
                                                type="text"
                                                value={form.category}
                                                onChange={(e) => setForm((f) => ({...f, category: e.target.value}))}
                                                placeholder="e.g. Hormone Health"
                                                className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>

                                        {/* SKU (super admin only) */}
                                        {isSuperAdmin && (
                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    SKU{' '}
                                                    <span className="font-normal text-mld-muted-text">(optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    maxLength={64}
                                                    value={form.sku ?? ''}
                                                    onChange={(e) =>
                                                        setForm((f) => ({...f, sku: e.target.value || null}))
                                                    }
                                                    placeholder="e.g. MLD-THYROID-KIT"
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                                <p className="mt-1 text-[11px] text-mld-muted-text">
                                                    Must match the WooCommerce and ELIS SKU for this product.
                                                </p>
                                            </div>
                                        )}

                                        {/* Prices */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Price ($)
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={priceInput}
                                                    onChange={(e) => setPriceInput(e.target.value)}
                                                    placeholder="0.00"
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm font-mono focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Member Price{' '}
                                                    <span className="font-normal text-mld-muted-text">(optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={memberPriceInput}
                                                    onChange={(e) => setMemberPriceInput(e.target.value)}
                                                    placeholder="0.00"
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm font-mono focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                            </div>
                                        </div>

                                        {/* ── Subscription cadence ── */}
                                        <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background/40 p-4">
                                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-mld-text-secondary">
                                                Subscription cadence
                                            </h4>
                                            <p className="mb-3 text-[11px] text-mld-muted-text">
                                                Drives the per-product membership math. One-time products contribute $0
                                                to the monthly bill; subscriptions amortize their price across the
                                                cadence.
                                            </p>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                        Cadence
                                                    </label>
                                                    <select
                                                        value={(() => {
                                                            const days = form.default_cadence_days ?? null;
                                                            const preset = CADENCE_OPTIONS.find(
                                                                (o) => o.value === days
                                                            );
                                                            return preset ? String(preset.value ?? 'null') : 'custom';
                                                        })()}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v === 'null') {
                                                                setForm((f) => ({...f, default_cadence_days: null}));
                                                            } else if (v === 'custom') {
                                                                setForm((f) => ({
                                                                    ...f,
                                                                    default_cadence_days: f.default_cadence_days ?? 60,
                                                                }));
                                                            } else {
                                                                setForm((f) => ({
                                                                    ...f,
                                                                    default_cadence_days: Number(v),
                                                                }));
                                                            }
                                                        }}
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none">
                                                        {CADENCE_OPTIONS.map((o) => (
                                                            <option
                                                                key={o.label}
                                                                value={o.value === null ? 'null' : String(o.value)}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                        <option value="custom">Custom (days)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                        Custom cadence (days)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={form.default_cadence_days ?? ''}
                                                        onChange={(e) => {
                                                            const n = Number(e.target.value);
                                                            setForm((f) => ({
                                                                ...f,
                                                                default_cadence_days: n > 0 ? n : null,
                                                            }));
                                                        }}
                                                        placeholder="e.g. 60"
                                                        disabled={form.default_cadence_days == null}
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none disabled:bg-mld-menu-background disabled:text-mld-muted-text"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                        Cadence label{' '}
                                                        <span className="font-normal text-mld-muted-text">
                                                            (patient-facing)
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={40}
                                                        value={form.cadence_label ?? ''}
                                                        onChange={(e) =>
                                                            setForm((f) => ({
                                                                ...f,
                                                                cadence_label: e.target.value || null,
                                                            }))
                                                        }
                                                        placeholder="e.g. Quarterly"
                                                        disabled={form.default_cadence_days == null}
                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none disabled:bg-mld-menu-background disabled:text-mld-muted-text"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Supply per shipment{' '}
                                                    <span className="font-normal text-mld-muted-text">(optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    maxLength={60}
                                                    value={form.supply_per_shipment ?? ''}
                                                    onChange={(e) =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            supply_per_shipment: e.target.value || null,
                                                        }))
                                                    }
                                                    placeholder='e.g. "30 doses" or "90-day supply"'
                                                    disabled={form.default_cadence_days == null}
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-mld-primary focus:outline-none disabled:bg-mld-menu-background disabled:text-mld-muted-text"
                                                />
                                            </div>
                                        </div>

                                        {/* Included assays (super admin only) */}
                                        {isSuperAdmin && (
                                            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background/40 p-4">
                                                <RecommendedListPicker
                                                    title="Included Assays"
                                                    description="Lab assays this product bundles. ELIS resolves the SKU to the same assay list — keep this in sync with the ELIS product build."
                                                    selectedKeys={(form.assays ?? []).map((a) => a.assay_code)}
                                                    available={assayOptions}
                                                    onChange={(keys) =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            assays: keys.map((key, idx) => ({
                                                                assay_code: key,
                                                                sort_order: idx,
                                                            })),
                                                        }))
                                                    }
                                                    placeholder="Add an assay..."
                                                    emptyText="No assays — this product ships without lab tests."
                                                />
                                            </div>
                                        )}

                                        {/* Image */}
                                        <div>
                                            <label className="mb-2 block text-xs font-medium text-mld-text-secondary">
                                                Image
                                            </label>

                                            {hasImage && (
                                                <div className="flex items-start gap-4 rounded-lg border border-mld-panel-border bg-mld-menu-background p-3">
                                                    <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-mld-panel-border bg-white">
                                                        {imageLoadFailed ? (
                                                            <ImageOff className="h-6 w-6 text-mld-muted-text" />
                                                        ) : (
                                                            <img
                                                                src={currentImageSrc}
                                                                alt={
                                                                    form.name
                                                                        ? `${form.name} product image`
                                                                        : 'Product image'
                                                                }
                                                                className="h-full w-full object-contain"
                                                                onError={() => setImageLoadFailed(true)}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-mld-text-primary">
                                                            {imageFile
                                                                ? imageFile.name
                                                                : imageLoadFailed
                                                                  ? "Image can't be loaded"
                                                                  : 'Current image'}
                                                        </p>
                                                        <p
                                                            className="mt-0.5 truncate text-xs text-mld-muted-text"
                                                            title={imageFile ? undefined : (form.image_url ?? '')}>
                                                            {imageFile
                                                                ? 'New image — uploads when you save'
                                                                : (form.image_url ?? '')}
                                                        </p>
                                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                                            {!showImagePicker && (
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    iconLeft={<Upload className="h-3.5 w-3.5" />}
                                                                    onClick={startReplaceImage}>
                                                                    Replace
                                                                </Button>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-mld-danger hover:bg-mld-danger/10 hover:text-mld-danger"
                                                                iconLeft={<Trash2 className="h-3.5 w-3.5" />}
                                                                onClick={removeImage}>
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {showImagePicker && (
                                                <div className={clsx(hasImage && 'mt-3')}>
                                                    <div className="mb-2 flex items-center gap-2">
                                                        <span className="text-xs text-mld-muted-text">
                                                            {hasImage ? 'Replace with' : 'Add an image'}
                                                        </span>
                                                        <div className="flex rounded-md border border-mld-panel-border bg-mld-menu-background p-0.5 text-[10px]">
                                                            <button
                                                                type="button"
                                                                onClick={() => setImageMode('upload')}
                                                                className={clsx(
                                                                    'rounded px-2 py-0.5 font-medium transition',
                                                                    imageMode === 'upload'
                                                                        ? 'bg-white text-mld-primary shadow-sm'
                                                                        : 'text-mld-muted-text'
                                                                )}>
                                                                Upload
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setImageMode('url')}
                                                                className={clsx(
                                                                    'rounded px-2 py-0.5 font-medium transition',
                                                                    imageMode === 'url'
                                                                        ? 'bg-white text-mld-primary shadow-sm'
                                                                        : 'text-mld-muted-text'
                                                                )}>
                                                                URL
                                                            </button>
                                                        </div>
                                                        {hasImage && (
                                                            <button
                                                                type="button"
                                                                onClick={cancelReplaceImage}
                                                                // Distinguished from the modal's own Cancel button.
                                                                aria-label="Cancel replacing image"
                                                                className="ml-auto text-xs font-medium text-mld-muted-text hover:text-mld-menu-text">
                                                                Cancel
                                                            </button>
                                                        )}
                                                    </div>
                                                    {imageMode === 'upload' ? (
                                                        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-mld-panel-border px-4 py-4 text-sm text-mld-muted-text hover:border-mld-primary hover:text-mld-primary">
                                                            <Upload className="h-4 w-4" />
                                                            Choose image...
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpeg,image/webp,image/gif"
                                                                className="hidden"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) {
                                                                        stageImageFile(file);
                                                                        setPickerOpen(false);
                                                                    }
                                                                    // Let the same file be re-picked after a Remove.
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </label>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={form.image_url ?? ''}
                                                            onChange={(e) => {
                                                                setImageLoadFailed(false);
                                                                setForm((f) => ({...f, image_url: e.target.value}));
                                                            }}
                                                            placeholder="https://..."
                                                            className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Internal notes (super admin only) */}
                                        {isSuperAdmin && (
                                            <div>
                                                <label className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Internal Notes{' '}
                                                    <span className="font-normal text-mld-muted-text">
                                                        (visible to super admins only)
                                                    </span>
                                                </label>
                                                <textarea
                                                    value={form.admin_notes ?? ''}
                                                    onChange={(e) =>
                                                        setForm((f) => ({...f, admin_notes: e.target.value || null}))
                                                    }
                                                    rows={3}
                                                    placeholder="Why this product exists, ELIS mapping details, supplier notes..."
                                                    className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={isSaving}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                            {editingId != null ? 'Save Changes' : 'Create Product'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Delete Confirmation Modal */}
            <Transition appear show={deleteConfirmId != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setDeleteConfirmId(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-sm rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="px-6 py-5">
                                        <Dialog.Title className="text-base font-semibold text-mld-menu-text">
                                            Delete Product
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-mld-muted-text">
                                            This will remove the product from the storefront and API. Continue?
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setDeleteConfirmId(null)}
                                            className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                deleteConfirmId != null && deleteMutation.mutate(deleteConfirmId)
                                            }
                                            disabled={deleteMutation.isPending}
                                            className={clsx(
                                                'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60',
                                                'bg-mld-danger/50 hover:bg-mld-danger'
                                            )}>
                                            {deleteMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Delete
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

/**
 * Compact $ icon button + popover for editing per-org price overrides
 * on a global product. Local input state; commits to the server via
 * the supplied `onSave`. Pass null for either field to clear that
 * override and revert to the global default.
 */
const PriceOverridePopover: React.FC<{
    product: StorefrontProduct;
    onSave: (
        priceCents: number | null,
        memberCents: number | null,
        billingType: string,
        coveragePct: number | null
    ) => void;
}> = ({product, onSave}) => {
    const [open, setOpen] = useState(false);
    const initialPrice =
        product.org_state?.price_override_cents != null
            ? (product.org_state.price_override_cents / 100).toFixed(2)
            : '';
    const initialMember =
        product.org_state?.member_price_override_cents != null
            ? (product.org_state.member_price_override_cents / 100).toFixed(2)
            : '';
    const [priceDraft, setPriceDraft] = useState(initialPrice);
    const [memberDraft, setMemberDraft] = useState(initialMember);
    const [billingType, setBillingType] = useState<string>(product.org_state?.billing_type ?? 'patient_full');
    const [coverage, setCoverage] = useState<string>(
        product.org_state?.employer_coverage_percent != null ? String(product.org_state.employer_coverage_percent) : ''
    );

    return (
        <Menu as="div" className="relative inline-block">
            <Menu.Button
                onClick={() => setOpen(true)}
                title="Override price for your org"
                className="inline-flex items-center gap-1 rounded-md bg-mld-warning/10 px-3 py-1 text-xs font-semibold text-mld-warning hover:bg-mld-warning hover:text-white">
                <DollarSign className="h-3 w-3" />
                <span>Override</span>
            </Menu.Button>
            {open && (
                <Menu.Items
                    static
                    className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-mld-panel-border bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-mld-text-secondary">
                        Override price for your org
                    </div>
                    <p className="mb-3 text-[10px] text-mld-muted-text">
                        Default: ${(product.price_cents / 100).toFixed(2)}
                        {product.member_price_cents != null && (
                            <> · Member: ${(product.member_price_cents / 100).toFixed(2)}</>
                        )}
                    </p>
                    <div className="space-y-2">
                        <div>
                            <label className="mb-0.5 block text-[10px] font-medium text-mld-text-secondary">
                                Price override ($)
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={priceDraft}
                                onChange={(e) => setPriceDraft(e.target.value)}
                                placeholder="leave blank to use default"
                                className="w-full rounded border border-mld-panel-border px-2 py-1 text-xs focus:border-mld-primary focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="mb-0.5 block text-[10px] font-medium text-mld-text-secondary">
                                Member price override ($)
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={memberDraft}
                                onChange={(e) => setMemberDraft(e.target.value)}
                                placeholder="leave blank to use default"
                                className="w-full rounded border border-mld-panel-border px-2 py-1 text-xs focus:border-mld-primary focus:outline-none"
                            />
                        </div>
                    </div>
                    <label className="block text-sm font-medium text-mld-text-primary mt-3">Employer billing</label>
                    <select
                        value={billingType}
                        onChange={(e) => setBillingType(e.target.value)}
                        className="w-full rounded border border-mld-panel-border px-2 py-1 text-xs focus:border-mld-primary focus:outline-none mt-1">
                        <option value="patient_full">Patient pays full</option>
                        <option value="employer_full">Employer covers full</option>
                        <option value="employer_partial">Employer covers %</option>
                    </select>
                    {billingType === 'employer_partial' && (
                        <input
                            type="number"
                            min={0}
                            max={100}
                            value={coverage}
                            onChange={(e) => setCoverage(e.target.value)}
                            placeholder="Coverage %"
                            className="w-full rounded border border-mld-panel-border px-2 py-1 text-xs focus:border-mld-primary focus:outline-none mt-2"
                        />
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                onSave(null, null, 'patient_full', null);
                                setPriceDraft('');
                                setMemberDraft('');
                                setBillingType('patient_full');
                                setCoverage('');
                                setOpen(false);
                            }}
                            className="text-[11px] text-mld-danger hover:underline">
                            Clear overrides
                        </button>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded border border-mld-panel-border px-2 py-1 text-[11px] text-mld-text-secondary hover:bg-mld-menu-background">
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const p = priceDraft.trim() ? Math.round(parseFloat(priceDraft) * 100) : null;
                                    const m = memberDraft.trim() ? Math.round(parseFloat(memberDraft) * 100) : null;
                                    const coveragePct = coverage.trim() ? parseInt(coverage, 10) : null;
                                    onSave(p, m, billingType, coveragePct);
                                    setOpen(false);
                                }}
                                className="rounded bg-mld-primary px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-mld-primary/90">
                                Save
                            </button>
                        </div>
                    </div>
                </Menu.Items>
            )}
        </Menu>
    );
};

export default StorefrontProductBuilder;
